import React, { useEffect, useState } from "react";
import "./PatientDetails.css";
import { useTranslation } from "react-i18next";
import Icons from "../../../../Assets/Icons";
import { format } from "date-fns";
import { getSecurePreviewUrl, openSecureFileUrl } from "../../../../utils/secureFilePreview";
import ToastPopup from "../../../../widgets/Toast/toastPopup";

const buildAddressFields = (address) => {
  if (!address || typeof address === "string") {
    return {
      personalCountryCode: "",
      personalPhoneNumber: "",
      officeCountryCode: "",
      officePhoneNumber: "",
      street: typeof address === "string" ? address : "",
      ward: "",
      district: "",
      city: "",
      province: "",
      postalCode: "",
      addressTag: "Home",
    };
  }

  return {
    personalCountryCode: address.personalCountryCode || "",
    personalPhoneNumber: address.personalPhoneNumber || "",
    officeCountryCode: address.officeCountryCode || "",
    officePhoneNumber: address.officePhoneNumber || "",
    street: address.street || "",
    ward: address.ward || "",
    district: address.district || "",
    city: address.city || "",
    province: address.province || "",
    postalCode: address.postalCode || "",
    addressTag: address.addressTag || "Home",
  };
};

const getAddressDisplayValue = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address;
  return [address.street, address.ward, address.district, address.city, address.province, address.postalCode].filter(Boolean).join(", ");
};

const getPhoneValue = (countryCode, phoneNumber) => {
  if (!countryCode || !phoneNumber) return "";
  return `${countryCode} ${phoneNumber}`;
};

const splitPhoneValue = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return { countryCode: "", phoneNumber: "" };
  const [countryCode, ...phoneParts] = trimmed.split(/\s+/);
  return {
    countryCode,
    phoneNumber: phoneParts.join(" "),
  };
};

const AddressField = ({ label, name, value, onChange, placeholder, error, required }) => (
  <div className="pd-address-edit-field">
    <label>{label}{required && <span className="asterik"> *</span>}</label>
    <input className={`pd-address-edit-input${error ? " pd-address-edit-input--error" : ""}`} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(name, e.target.value)} />
    {error && <span className="pd-address-field-error">{error}</span>}
  </div>
);

const AttachmentImagePreview = ({ attachment }) => {
  const [src, setSrc] = useState(attachment.url);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setShowFallback(false);
    getSecurePreviewUrl(attachment.url)
      .then((previewUrl) => {
        if (!cancelled) setSrc(previewUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setSrc(attachment.url);
          setShowFallback(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attachment.url]);

  useEffect(() => {
    return () => {
      if (src?.startsWith("blob:")) URL.revokeObjectURL(src);
    };
  }, [src]);

  return (
    <>
      {!showFallback && <img src={src} className="attachment-image-fit" alt={attachment.name} onError={() => setShowFallback(true)} />}
      {showFallback && (
        <div className="attachment-preview-fallback" style={{ display: "flex" }}>
          Preview unavailable
        </div>
      )}
    </>
  );
};

const PatientDetails = ({ setPage, setEnabledSteps, data, onReview, onNext, readOnly = false, rejectReason, rejectedAt }) => {
  const { t } = useTranslation();
  const rawDate = data?.dateOfBirth || null;
  const [patientCondition, setPatientCondition] = useState(data?.patientCondition || "");
  const [patientAddress, setPatientAddress] = useState(data?.addressFields || buildAddressFields(data?.address));
  const [addressDraft, setAddressDraft] = useState(data?.addressFields || buildAddressFields(data?.address));
  const [isAddressEditorOpen, setIsAddressEditorOpen] = useState(false);
  const [addressFieldErrors, setAddressFieldErrors] = useState({});
  const [description, setDescription] = useState(data?.description || "");
  const [zone, setZone] = useState("");
  const [localStatus, setLocalStatus] = useState(data?.status || "OPEN");

  const attachments = data?.attachments || [];

  useEffect(() => {
    const nextAddress = data?.addressFields || buildAddressFields(data?.address);
    setPatientCondition(data?.patientCondition || "");
    setPatientAddress(nextAddress);
    setAddressDraft(nextAddress);
    setIsAddressEditorOpen(false);
    setDescription(data?.description || "");
    setZone(data?.distanceInKm ? String(data.distanceInKm) : "");
    setLocalStatus(data?.status || "OPEN");
  }, [data?.patientCondition, data?.address, data?.addressFields, data?.description, data?.distanceInKm, data?.status]);

  const getFormattedDate = (date) => {
    if (!date) return "-";
    return format(new Date(date), "dd MMM, yyyy");
  };

  const handleReviewAction = () => {
    onReview?.({
      patientCondition: patientCondition.trim(),
      addressFields: patientAddress,
      addressText: getAddressDisplayValue(patientAddress),
      description: description.trim(),
      zone,
    }).then(() => {
      setLocalStatus("IN_REVIEW");
    }).catch(() => {
      // Error handled by parent
    });
  };

  const handleNextAction = () => {
    const parsedDistance = Number(zone);
    if (zone === "" || zone === null || zone === undefined || Number.isNaN(parsedDistance) || parsedDistance <= 0) {
      ToastPopup.error(t("distanceKmRequired"));
      return;
    }
    onNext?.({
      patientCondition: patientCondition.trim(),
      addressFields: patientAddress,
      addressText: getAddressDisplayValue(patientAddress),
      description: description.trim(),
      zone,
    });
  };

  const handleOpenAddressEditor = () => {
    setAddressDraft(patientAddress);
    setIsAddressEditorOpen(true);
  };

  const handleAddressDraftChange = (name, value) => {
    if (addressFieldErrors[name]) {
      setAddressFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    setAddressDraft((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelAddressEdit = () => {
    setAddressDraft(patientAddress);
    setAddressFieldErrors({});
    setIsAddressEditorOpen(false);
  };

  const handleSaveAddressEdit = () => {
    const phoneDisplay = addressDraft.personalPhoneNumberDisplay ?? getPhoneValue(addressDraft.personalCountryCode, addressDraft.personalPhoneNumber);
    const errors = {};
    if (!phoneDisplay?.trim()) errors.personalPhoneNumberDisplay = t("pleaseEnter", { fieldName: t("phoneNumber") });
    if (!addressDraft.street?.trim()) errors.street = t("enterStreet");
    if (!addressDraft.ward?.trim()) errors.ward = t("enterWard");
    if (!addressDraft.city?.trim()) errors.city = t("cityReq");
    if (!addressDraft.province?.trim()) errors.province = t("provinceReq");
    if (!addressDraft.postalCode) errors.postalCode = t("postalCodeInvalid");
    if (!addressDraft.district?.trim()) errors.district = t("districtReq");

    if (Object.keys(errors).length > 0) {
      setAddressFieldErrors(errors);
      return;
    }

    setAddressFieldErrors({});
    const personalPhone = splitPhoneValue(phoneDisplay);
    const officePhone = splitPhoneValue(addressDraft.officePhoneNumberDisplay ?? getPhoneValue(addressDraft.officeCountryCode, addressDraft.officePhoneNumber));
    const nextAddress = {
      ...addressDraft,
      personalCountryCode: personalPhone.countryCode,
      personalPhoneNumber: personalPhone.phoneNumber,
      officeCountryCode: officePhone.countryCode,
      officePhoneNumber: officePhone.phoneNumber,
    };
    delete nextAddress.personalPhoneNumberDisplay;
    delete nextAddress.officePhoneNumberDisplay;
    setPatientAddress(nextAddress);
    setIsAddressEditorOpen(false);
  };

  const isAwaiting = localStatus !== "ASSIGNED";
  const isFieldsDisabled = readOnly || isAwaiting;

  const isNextEnabled = !isAwaiting;

  return (
    <div className="verification-request">
      {/* ── Patient Info Card ── */}
      <div className="pdl-info verify">
        <div className="pd-title-row">
          <p className="pdl-title">{t("personalInformation")}</p>
          {isAwaiting && (
            <button type="button" className="verify-btn" onClick={handleReviewAction}>
              {t("review") || "Review"}
            </button>
          )}
        </div>
        <div className="pdl-body">
          {/* Left: photo + HN Number */}
          <div className="pdl-section-left">
            <img src={data?.profilePhoto || Icons.noProfile} alt="profile" />
            <div className="pdl-hn pd-hn-center">
              <p className="pdl-hn-title">
                {t("hnNumber")}{" "}
                <span className="asterik">*</span>
              </p>
              <p className="pdl-number">{data?.hnNumber || "-"}</p>
            </div>
          </div>

          {/* Right: name → gender+dob → phone+email → insurance → condition+distance */}
          <div className="pdl-section-right">
            <p className="pdl-name">{data?.name}</p>

            <section className="pdl-phone-dob nc-sd-patient">
              <div className="pdl-detail">
                <img src={Icons.gender} alt="gender" />
                <span>{data?.gender ? t(data.gender) : "-"}</span>
              </div>
              <div className="pdl-detail">
                <img src={Icons.CalenderIcon} alt="dob" />
                <span>{getFormattedDate(rawDate)}</span>
              </div>
            </section>

            <section className="pdl-phone-dob nc-sd-patient">
              <div className="pdl-detail">
                <img src={Icons.Call} alt="phone" />
                <span>{data?.phone}</span>
              </div>
              <div className="pdl-detail">
                <img src={Icons.SmsIcon} alt="email" />
                <span>{data?.email || "-"}</span>
              </div>
            </section>

            <div className="sd-hn pd-insurance-row">
              <img src={Icons.insurance} alt="insurance" />
              <span>{data?.insuranceType ? t(data.insuranceType) : "-"}</span>
            </div>

            {/* Patient Condition + Distance by KM */}
            <section className="pdl-phone-dob nc-sd-dropdown pd-condition-zone-row">
              <div className="pdl-hn pd-field-half">
                <p className="pdl-hn-title">{t("patientCondition")}</p>
                <input
                  type="text"
                  className="form-section-input-2"
                  value={patientCondition}
                  onChange={(e) => setPatientCondition(e.target.value)}
                  placeholder={t("patientCondition")}
                  disabled={isFieldsDisabled}
                />
              </div>
              <div className="pdl-hn pd-field-half">
                <p className="pdl-hn-title">
                  {t("zone")}
                  <span className="asterik"> *</span>
                </p>
                <input type="number" className="form-section-input-2" value={zone || ""} onChange={(e) => setZone(e.target.value)} placeholder={t("noofkm")} min="0" disabled={isFieldsDisabled} />
              </div>
            </section>

            {/* Service Address */}
            <div className="pdl-hn pd-address-block">
              <div className="pd-address-header-row">
                <p className="pdl-hn-title">{t("serviceAddress") || "Service Address"}</p>
                {!readOnly && (
                  <button type="button" className="verify-btn" onClick={handleOpenAddressEditor} disabled={isFieldsDisabled}>
                    {t("editAddress")}
                  </button>
                )}
              </div>
              <div className="pdl-detail pd-address-text pd-address-input-wrap">
                <img src={Icons.Location} alt="location" />
                <p className="pd-address-readonly">{getAddressDisplayValue(patientAddress) || t("notAvailable")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isAddressEditorOpen && (
        <div className="whp-container pd-address-editor-section">
          <h2>{t("address")}</h2>
          <div className="pd-address-edit-grid">
            <AddressField label={t("phoneNumber")} name="personalPhoneNumberDisplay" value={addressDraft.personalPhoneNumberDisplay ?? getPhoneValue(addressDraft.personalCountryCode, addressDraft.personalPhoneNumber)} placeholder="84 123456789" onChange={handleAddressDraftChange} error={addressFieldErrors.personalPhoneNumberDisplay} required />
            {/* <AddressField label={t("officeNumber")} name="officePhoneNumberDisplay" value={addressDraft.officePhoneNumberDisplay ?? getPhoneValue(addressDraft.officeCountryCode, addressDraft.officePhoneNumber)} placeholder={t("notAvailable")} onChange={handleAddressDraftChange} /> */}
            <AddressField label={t("street")} name="street" value={addressDraft.street || ""} placeholder={t("notAvailable")} onChange={handleAddressDraftChange} error={addressFieldErrors.street} required />
            <AddressField label={t("ward")} name="ward" value={addressDraft.ward || ""} placeholder={t("notAvailable")} onChange={handleAddressDraftChange} error={addressFieldErrors.ward} required />
            <AddressField label={t("district")} name="district" value={addressDraft.district || ""} placeholder={t("notAvailable")} onChange={handleAddressDraftChange} error={addressFieldErrors.district} required />
            <AddressField label={t("city")} name="city" value={addressDraft.city || ""} placeholder={t("notAvailable")} onChange={handleAddressDraftChange} error={addressFieldErrors.city} required />
            <AddressField label={t("province")} name="province" value={addressDraft.province || ""} placeholder={t("notAvailable")} onChange={handleAddressDraftChange} error={addressFieldErrors.province} required />
            <AddressField label={t("postalCode")} name="postalCode" value={addressDraft.postalCode || ""} placeholder={t("notAvailable")} onChange={handleAddressDraftChange} error={addressFieldErrors.postalCode} required />
            {/* <AddressField label="Address Tag" name="addressTag" value={addressDraft.addressTag || "Home"} onChange={handleAddressDraftChange} /> */}
          </div>
          <div className="pd-address-editor-actions">
            <button type="button" className="form-section-cancel" onClick={handleCancelAddressEdit}>
              {t("cancel")}
            </button>
            <button type="button" className="form-section-create" onClick={handleSaveAddressEdit}>
              {t("save")}
            </button>
          </div>
        </div>
      )}

      {/* Attachments — read-only view (no add/remove) */}
      {attachments.length > 0 && (
        <div className="whp-container attachments-section">
          <div className="title-hr2">
            <p>{t("attachments")}</p>
            <hr />
          </div>
          <div className="attachments-grid">
            {attachments.map((att, i) => (
              <div key={i} className="attachment-item">
                <div className="attachment-preview" onClick={() => openSecureFileUrl(att.url)}>
                  {att.type !== "image" ? <img src={Icons.PdfRedIcon} className="pdf-icon-small" alt="PDF" /> : <AttachmentImagePreview attachment={att} />}
                </div>
                <span className="attachment-name">{att.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="whp-container notes-section">
        <div className="title-hr2">
          <p>{t("description")}</p>
          <hr />
        </div>
        <div className="notes-content">
            <textarea
              className="form-section-input-2 notes-textarea"
              value={description || ""}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("description")}
              rows={4}
              disabled={isFieldsDisabled}
            />
        </div>
      </div>

      {(data?.rejectReason || rejectReason) && (
        <div className="whp-container notes-section" style={{ borderLeft: "3px solid #e53935", background: "var(--main-bg)" }}>
          <div className="title-hr2">
            <p style={{ color: "#e53935" }}>{t("rejectionReason") || "Rejection Reason"}</p>
            <hr />
          </div>
          <div className="notes-content" style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <p style={{ fontSize: "1.3rem", color: "#1a1a1a" }}>{data?.rejectReason || rejectReason}</p>
            {(data?.rejectedAt || rejectedAt) && (
              <p style={{ fontSize: "1.2rem", color: "#888" }}>
                {new Date(data?.rejectedAt || rejectedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                {" at "}
                {new Date(data?.rejectedAt || rejectedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}
              </p>
            )}
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="patient-details-footer">
        <button className="form-section-create" onClick={handleNextAction} disabled={!isNextEnabled}>
          {t("saveAndNext") || "Save and Next"}
        </button>
        </div>
      )}
    </div>
  );
};

export default PatientDetails;
